import { getPreset, getSourceId, RegistrationFlow, Scenario, Touchpoint } from '@account/account-portal/config'
import { createUserPassword, registerUser, usePasswordCreateStatus } from '@account/account-portal/lib'
import { RegistrationError, RegistrationData } from '@account/account-portal/types'
import { GlButton } from '@adl/component-button'
import { GlForm } from '@adl/component-form'
import { useConfig } from '@crystal/config'
import { useDevice } from '@crystal/core'
import { useFeatureFlags } from '@crystal/feature-flags'
import { FormState, useFormValidation } from '@crystal/form'
import { Translations, useTranslations } from '@crystal/translator'
import { useEffect, useState } from 'react'

import { AccountPortalConfig } from '../config/index'
import { useConsents, useViewData } from '../lib'
import { trackFullAccountCreationStart, trackFullAccountCreationSuccess } from '../lib/analytics'

import styles from './account-portal.module.scss'
import { FormContainer } from './layout/FormContainer'
import { PasswordInput } from './PasswordInput'

// API field map
const fieldNameMap: Record<string, string> = {
  'account.email': 'email',
}

export interface PasswordCreationProps {
  consentsState: ReturnType<typeof useConsents>
}

export const PasswordCreation = ({ consentsState }: PasswordCreationProps) => {
  const t = useTranslations<Translations>()
  const { LIGHT_ACCOUNT_DISABLED } = useFeatureFlags()
  const { passwordPolicyRegistration, legalPolicy } = useConfig<AccountPortalConfig>()
  const { isPending } = usePasswordCreateStatus()
  const { registerField, handleSubmit, formState, setError } = useFormValidation<RegistrationData>()
  const { errors } = formState
  const {
    viewData: { persistentLogin, accessToken, refreshToken, preset, email, minAgeConfirmation },
  } = useViewData()
  const [generalError, setGeneralError] = useState<string>()
  const { isMobile } = useDevice()
  const registrationSourceId = getSourceId(Touchpoint.Unknown, Scenario.Registration)

  useEffect(() => {
    showValidationErrors(formState)
  }, [formState])

  function showValidationErrors({ errors }: FormState<RegistrationData>) {
    if (!Object.keys(errors).length) {
      return
    }

    const errorMessages: Record<string, string> = {
      email: t('email.error'),
      password: t('password.error', passwordPolicyRegistration.minLength, passwordPolicyRegistration.minBigLetters),
    }

    Object.entries(errors)
      .filter(([field, error]) => !error.message && errorMessages[field])
      .map(([field]) => [field, errorMessages[field]])
      .forEach(([field, message]) => setError(field as keyof RegistrationData, { message }))
  }

  function showApiErrors({ validationErrors }: RegistrationError) {
    if (validationErrors) {
      setGeneralError('')
      const errorMessages: Record<string, string> = {
        EMAIL_ALREADY_EXISTS: t('apiError.EMAIL_ALREADY_EXISTS'),
      }

      Object.entries(validationErrors)
        .map(([key, error]) => [fieldNameMap[key], errorMessages[error.errorCode]])
        .forEach(([field, message]) => setError(field as keyof RegistrationData, { message }))
      return
    }
    setGeneralError(t('unknownError.message'))
  }

  const onSubmit = async ({ password }: { password: string }) => {
    const { registrationFlow } = getPreset(preset)

    if ((LIGHT_ACCOUNT_DISABLED || registrationFlow === RegistrationFlow.FullAccount) && email) {
      return registerUser({
        email,
        password,
        ageConfirmation: legalPolicy.minAgeRequired ? minAgeConfirmation : true,
        legalAcceptance: consentsState?.payload.legalAcceptance,
        persistentLogin: !!persistentLogin,
        sourceId: registrationSourceId,
      })
        .then(() => trackFullAccountCreationSuccess())
        .catch(err => showApiErrors(err as RegistrationError))
    } else if (accessToken) {
      return createUserPassword({
        password,
        access_token: accessToken,
        refresh_token: refreshToken,
        persistentLogin: !!persistentLogin,
      })
        .then(() => trackFullAccountCreationSuccess())
        .catch(err => showApiErrors(err as RegistrationError))
    }
    throw new Error('Invalid accessToken')
  }

  const isFocusedFirstTime = (fieldName: keyof RegistrationData) => {
    return !formState.touchedFields?.[fieldName]
  }

  const onFocus = () => {
    if (isFocusedFirstTime('password')) {
      trackFullAccountCreationStart()
    }
  }

  return (
    <FormContainer>
      <GlForm noValidate onSubmit={handleSubmit(onSubmit)}>
        <h1 className={styles['title']}>{t('register.title')}</h1>
        <p className={styles['subtitle']}>{t('register.subtitle')}</p>
        <PasswordInput
          id="password"
          type="password"
          autoComplete="current-password"
          className="gl-form-item"
          labelText={t('password.label')}
          aria-label={t('password.label')}
          onFocusCapture={() => onFocus()}
          {...registerField('password', {
            ...passwordPolicyRegistration,
            errorMessage: t('password.error'),
          })}
          hintText={t('password.error')}
          errorText={(errors.password?.message as string) || t('password.error')}
        />
        <GlButton
          type="submit"
          fullWidth={isMobile}
          className="gl-vspace-bpall-small"
          aria-label={t('registerButton.label')}
          loading={isPending}
          disabled={isPending}
          id="registration-submit-button">
          {t('registerButton.label')}
        </GlButton>
        {generalError && <p className={styles['error']}>{generalError}</p>}
      </GlForm>
    </FormContainer>
  )
}
